import { useTranslation } from 'react-i18next';

import type { CartCalculationOrderResponse } from '~/api/Catalog';
import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { useDateLang } from '~/hooks/useDateLang';
import { useModal } from '~/hooks/useModal';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import { getSelectedDaySplited } from '~/pages/Checkout/utils';
import { cartStore, mainStore, orderStore } from '~/stores';
import type Delivery from '~/stores/CheckoutStore/Delivery';
import { formatPriceWithCurrency } from '~/utils/formaters';

import { useSlots } from './useSlots';

interface UseDeliveryParams {
  delivery: Delivery;
  order: CartCalculationOrderResponse;
}

export const useDelivery = ({ delivery, order }: UseDeliveryParams) => {
  const { t } = useTranslation();
  const { locale } = useDateLang();
  const { timeFormat } = useTimeFormat();
  const { openModal } = useModal();

  const deliveryItems = cartStore.selectedCartItems.filter(({ sku }) =>
    delivery.itemsIds.includes(sku),
  );

  const { showSelectTimeButton } = useSlots({ deliveryItems });

  const deliveryPrice = formatPriceWithCurrency(
    mainStore.convertPenceToPounds(order.delivery_info.paid_price),
  );

  const isDeliveryFree = order.is_delivery_free;

  const freeDeliveryThreshold = order.delivery_info.threshold;

  const leftUntilFreeDelivery = formatPriceWithCurrency(
    mainStore.convertPenceToPounds(
      freeDeliveryThreshold - order.paid_without_delivery,
    ),
  );

  const progressBarWidth = `${
    (order.paid_without_delivery / freeDeliveryThreshold) * 100
  }%`;

  const getSelectedDay = () => {
    if (!delivery.slot) {
      return t('phrases:deliveryInMin', {
        time: orderStore.etaCalculation?.duration.range,
      });
    }
    return getSelectedDaySplited(delivery.slot, timeFormat, locale).date;
  };

  const getSelectedDayAndTime = () => {
    if (!delivery.slot) {
      return {
        day: t('phrases:deliveryInMin', {
          time: orderStore.etaCalculation?.duration.range,
        }),
        time: undefined,
      };
    }
    const { day, time } = getSelectedDaySplited(
      delivery.slot,
      timeFormat,
      locale,
    );
    return { day, time };
  };

  const openDeliveryModal = () => {
    openModal(ModalType.DeliveryInfo, 'checkout');
  };

  const deliveryTooltip = company.isDeliveryInfoModalEnabled;

  return {
    deliveryItems,
    showSelectTimeButton,
    deliveryPrice,
    isDeliveryFree,
    leftUntilFreeDelivery,
    progressBarWidth,
    getSelectedDay,
    getSelectedDayAndTime,
    openDeliveryModal,
    deliveryTooltip,
  };
};
